import { hasEntitlement, type EntitlementCode } from "./entitlements";

/** Free coach messages per day before soft gate (unlimited with Plus). */
export const FREE_COACH_MESSAGES_PER_DAY = 3;

const COACH_COUNT_KEY = "andrum.coach.free_messages";

type DayCount = { day: string; count: number };

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

function readCoachCount(): DayCount {
  try {
    const raw = localStorage.getItem(COACH_COUNT_KEY);
    if (!raw) return { day: todayKey(), count: 0 };
    const parsed = JSON.parse(raw) as DayCount;
    if (parsed.day !== todayKey()) return { day: todayKey(), count: 0 };
    return parsed;
  } catch {
    return { day: todayKey(), count: 0 };
  }
}

function writeCoachCount(data: DayCount) {
  localStorage.setItem(COACH_COUNT_KEY, JSON.stringify(data));
}

export function getFreeCoachMessagesUsed(): number {
  return readCoachCount().count;
}

export function getFreeCoachMessagesRemaining(): number {
  return Math.max(0, FREE_COACH_MESSAGES_PER_DAY - getFreeCoachMessagesUsed());
}

/** Call after a successful free-tier coach send. */
export function incrementFreeCoachMessage(): number {
  const current = readCoachCount();
  const next = { day: todayKey(), count: current.count + 1 };
  writeCoachCount(next);
  return next.count;
}

export async function canUsePremiumExperiment(): Promise<boolean> {
  return hasEntitlement("premium_experiments");
}

export async function canUseUnlimitedCoach(): Promise<boolean> {
  return hasEntitlement("coach_unlimited");
}

export async function canUsePremiumPrograms(): Promise<boolean> {
  return hasEntitlement("programs");
}

/**
 * Soft free-tier coach gate: unlimited if Plus, else daily cap.
 */
export async function canSendCoachMessage(): Promise<{
  ok: boolean;
  remaining: number;
  isPremium: boolean;
}> {
  const isPremium = await canUseUnlimitedCoach();
  if (isPremium) {
    return { ok: true, remaining: Infinity, isPremium: true };
  }
  const remaining = getFreeCoachMessagesRemaining();
  return { ok: remaining > 0, remaining, isPremium: false };
}

export type { EntitlementCode };
